import {
  Component,
  Input,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  ContentChildren,
  QueryList,
  Renderer2,
  TemplateRef,
  ChangeDetectorRef,
  OnInit,
  inject,
  afterNextRender,
  AfterContentInit,
  input,
} from "@angular/core";
import KeenSlider, {
  KeenSliderInstance,
  KeenSliderOptions,
  WebOptions,
} from "keen-slider";
import { SliderItem } from "src/app/models/slider-item.model";
import { ProductCompact } from "src/app/models/shared-models/product.model";
import { AppImage } from "src/app/models/shared-models/image.model";
import { YumpuDoc } from "../yumpu-viewer/yumpu-doc.model";
import { SlideComponent } from "./slide/slide.component";
import { FadeOnScrollDirective } from "@app/shared/fade-on-scroll.directive";
import { ScreenSizeService } from "@app/shared/screen-size.service";

export type SliderItemType =
  | SliderItem
  | ProductCompact
  | YumpuDoc
  | AppImage
  | TemplateRef<any>;

@Component({
  selector: "slider",
  templateUrl: "./slider.component.html",
  styleUrls: [
    "../../../../node_modules/keen-slider/keen-slider.min.css",
    "./slider.component.scss",
  ],
  standalone: true,
  imports: [SlideComponent, FadeOnScrollDirective],
})
export class SliderComponent implements OnInit, OnDestroy, AfterViewInit {
  private cdr = inject(ChangeDetectorRef);
  private renderer = inject(Renderer2);
  screenSizeService = inject(ScreenSizeService);

  @Input() slides: SliderItemType[] = [];

  @ViewChild("sliderRef", { static: true, read: ElementRef })
  sliderRef?: ElementRef<HTMLElement>;

  @ContentChildren(TemplateRef, { read: TemplateRef })
  slideComponents!: QueryList<TemplateRef<any>>;

  @Input() slidesPerView: number | "auto" = "auto";

  @Input() fullHeight = true;
  @Input() bookPage = false;
  @Input() autoplay = false;
  @Input() loop = true;
  @Input("showDots") showDotsInput: boolean = true;
  @Input("showArrows") showArrows: boolean = true;
  showDots = true;

  @Input() origin?: number | "center" | "auto";

  @Input() breakpoints?: {
    [key: string]: Omit<WebOptions<any>, "breakpoints">;
  };

  @Input() defaultNavbarColor = {
    background: "transparent",
    text: "transparent",
  };

  currentSlide: number = 0;
  dotHelper: Array<number> = [];
  slider: KeenSliderInstance | null = null;

  randomId = Math.random().toString(36).substring(7);

  hasTransparentNavbar(): boolean {
    const b = this.defaultNavbarColor.background;
    return b === "transparent" || b === undefined || b.trim() === "";
  }

  constructor() {}

  ngOnInit() {
    if (this.showDotsInput) {
      this.showDots = this.slides.length > 1;
    } else {
      this.showDots = false;
    }
  }

  ngAfterViewInit() {
    if (!this.screenSizeService.isBrowser) return;
    if (!this.slider) {
      this.initKeenSlider();
    }
  }

  initKeenSlider() {
    if (!this.slides.length) return;
    if (!this.sliderRef) return;

    // Build plugins array conditionally
    const plugins = [];

    // Only add autoplay plugin if autoplay is enabled
    if (this.autoplay) {
      plugins.push((slider: KeenSliderInstance) => {
        let timeout: NodeJS.Timeout;
        let mouseOver = false;

        function clearNextTimeout() {
          clearTimeout(timeout);
        }
        function nextTimeout() {
          clearTimeout(timeout);
          if (mouseOver) return;
          timeout = setTimeout(() => {
            slider.next();
          }, 4000);
        }

        slider.on("created", () => {
          const buttons = slider.container.querySelectorAll("button");

          buttons.forEach((button) => {
            button.addEventListener("mouseenter", () => {
              mouseOver = true;
              clearNextTimeout();
            });

            button.addEventListener("mouseleave", () => {
              mouseOver = false;
              nextTimeout();
            });
          });

          nextTimeout();
        });

        slider.on("dragStarted", clearNextTimeout);
        slider.on("animationEnded", nextTimeout);
        slider.on("updated", nextTimeout);
      });
    }

    this.slider = new KeenSlider(
      this.sliderRef.nativeElement,
      {
        loop: this.loop,
        drag: true,
        mode: "snap",
        dragSpeed: 1,
        renderMode: "performance",
        breakpoints: this.breakpoints,
        slides: {
          perView: this.slidesPerView,
          spacing: 40,
          origin: this.origin ?? "center",
          number: this.slides.length,
        },
        initial: this.currentSlide,
        created: (s) => {
          this.currentSlide = s.track.details.rel;
        },
        slideChanged: (s) => {
          if (this.currentSlide == s.track.details.rel) {
            return;
          }
          this.currentSlide = s.track.details.rel;
        },
      },
      plugins, // Use the conditionally built plugins array
    );

    this.dotHelper = [
      ...Array(this.slider?.track.details?.slides.length).keys(),
    ];
    this.cdr.detectChanges();
  }

  isLastSlide() {
    const progress = this.slider?.track.details?.progress;
    if (!progress) return false;
    return progress >= 0.98;
  }

  totalSlides() {
    return this.slides.length + this.slideComponents.length;
  }

  ngOnDestroy() {
    this.slider?.destroy();
  }

  prev() {
    this.slider?.prev();
  }

  next() {
    this.slider?.next();
  }
}
